/**
 * The page a customer's certificate-of-completion link opens (ADR 0308).
 *
 * `/cert/<token>`, the sibling of `/c/<token>`: no session, no row level
 * security to satisfy, no data of ours in the bundle. Everything comes from
 * the `completion-certificate` Edge Function, and if that function is
 * unreachable this page shows a phone number rather than a stack trace.
 *
 * The same page is what "Sign on this device" opens when the rep hands their
 * phone over, and what Docs opens to read one back, so there is one drawing
 * of the certificate and no second one to keep honest. It is drawn as the
 * paper the design was handed over as — a letter-size page — and prints as
 * one, which is how a phone saves it as a PDF.
 */

import {
  certificateDateWords,
  certificateSignatureIsComplete,
  certificateSigningSteps,
  nextCertificateStep,
  signedDateWords,
  type CompletionCertificate,
} from "@roof-hawk/core";
import { useCallback, useEffect, useState } from "react";

/** `/cert/<32 hex characters>` and nothing else. */
export function certificateTokenFromPath(pathname: string): string | null {
  const match = /^\/cert\/([0-9a-f]{32})\/?$/.exec(pathname);
  return match?.[1] ?? null;
}

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/completion-certificate`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface CertificateSignature {
  signerName: string;
  signerEmail: string | null;
  signedDate: string | null;
  signedAt: string;
}

type Loaded =
  | { state: "loading" }
  | { state: "open"; document: CompletionCertificate }
  | {
      state: "signed";
      document: CompletionCertificate;
      signature: CertificateSignature | null;
    }
  | { state: "closed"; message: string }
  | { state: "error" };

/** Today, as the customer's own device reckons it. Never through `new Date`. */
function todayLocal(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export function CertificatePage({ token }: { token: string }) {
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every call is a POST, reading included: the token is the only credential
  // in this flow and must stay out of logs, history and Referer headers.
  const call = useCallback(
    async (action: "open" | "sign", payload?: object) => {
      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: {
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, action, ...payload }),
      });
      return { response, body: await response.json() };
    },
    [token],
  );

  const take = (response: Response, body: Record<string, unknown>) => {
    if (response.ok && body.state === "open") {
      setLoaded({
        state: "open",
        document: body.document as CompletionCertificate,
      });
      return true;
    }
    if (response.ok && body.state === "signed") {
      setLoaded({
        state: "signed",
        document: body.document as CompletionCertificate,
        signature: (body.signature as CertificateSignature | null) ?? null,
      });
      return true;
    }
    return false;
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { response, body } = await call("open");
        if (cancelled) return;
        if (take(response, body)) return;
        if (body?.message)
          setLoaded({ state: "closed", message: body.message });
        else setLoaded({ state: "error" });
      } catch {
        if (!cancelled) setLoaded({ state: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [call]);

  const sign = async (payload: {
    consented: boolean;
    signerName: string;
    signerEmail: string;
    signedDate: string;
  }) => {
    setSigning(true);
    setError(null);
    try {
      const { response, body } = await call("sign", payload);
      if (!take(response, body)) {
        setError(
          body?.error ??
            body?.message ??
            "That did not go through. Please try again, or call us.",
        );
      }
    } catch {
      setError("That did not go through. Please try again, or call us.");
    } finally {
      setSigning(false);
    }
  };

  if (loaded.state === "loading") return <div className="pdoc-wait" />;

  if (loaded.state === "error") {
    return (
      <Message
        title="We cannot open this right now"
        body="Please call us and we will sort it out."
      />
    );
  }

  if (loaded.state === "closed") {
    return <Message title="This certificate" body={loaded.message} />;
  }

  return (
    <CertificateView
      document={loaded.document}
      signature={loaded.state === "signed" ? loaded.signature : null}
      onSign={sign}
      signing={signing}
      error={error}
    />
  );
}

/**
 * The certificate itself, signed or not.
 *
 * One component for both: a signed certificate shows the same page with the
 * customer's name on the homeowner line, so a customer coming back to it and
 * the office reading it in Docs are looking at the same thing.
 */
export function CertificateView({
  document,
  signature,
  onSign,
  readOnly = false,
  signing,
  error,
}: {
  document: CompletionCertificate;
  signature: CertificateSignature | null;
  onSign: (payload: {
    consented: boolean;
    signerName: string;
    signerEmail: string;
    signedDate: string;
  }) => void | Promise<void>;
  /** Office records can be opened before signing, but cannot be signed here. */
  readOnly?: boolean;
  signing?: boolean;
  error?: string | null;
}) {
  const signedOn = signature
    ? (signedDateWords(signature.signedDate) ??
      certificateDateWords(signature.signedAt.slice(0, 10)))
    : null;

  return (
    <div className="pdoc pdoc-cert">
      <CertificatePaper
        document={document}
        signature={signature}
        signedOn={signedOn}
      />
      <section className="pdoc-cert-signing">
        {signature ? (
          <div className="cdoc-signed">
            <p className="cdoc-signed-by">
              Signed by <strong>{signature.signerName}</strong>
              {signedOn ? ` on ${signedOn}` : ""}. This is your copy — print it
              or save it, and hand it to your insurance company if they ask for
              proof the work is done.
            </p>
            <button
              type="button"
              className="pdoc-toggle"
              onClick={() => window.print()}
            >
              Print or save
            </button>
          </div>
        ) : readOnly ? (
          <p className="cdoc-signed-by">Awaiting the customer's signature.</p>
        ) : (
          <SigningForm onSign={onSign} signing={signing} error={error} />
        )}

        {document.rep ? (
          <p className="cdoc-rep">
            Questions? Call {document.rep.name}
            {document.rep.phone ? ` on ${document.rep.phone}` : ""}.
          </p>
        ) : null}
      </section>
    </div>
  );
}

/** The letter-size page itself: what is frozen, and the signature lines. */
function CertificatePaper({
  document,
  signature,
  signedOn,
}: {
  document: CompletionCertificate;
  signature: CertificateSignature | null;
  signedOn: string | null;
}) {
  return (
    <section className="pdoc-cert-page">
      <header className="cert-head">
        <img
          src="/certificate/logo-horizontal.png"
          alt={document.company.name}
        />
        <div className="cert-company">
          {document.company.addressLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
          {document.company.phone ? <div>{document.company.phone}</div> : null}
        </div>
      </header>

      <h1>Certificate of Completion</h1>

      <dl className="cert-details">
        <dt>Homeowner</dt>
        <dd className="cert-name">{document.homeowner}</dd>
        <dt>Property Address</dt>
        <dd>{document.propertyAddress}</dd>
        <dt>Project Type</dt>
        <dd>{document.projectType}</dd>
        <dt>Date Completed</dt>
        <dd>{certificateDateWords(document.dateCompleted)}</dd>
      </dl>

      <h2>Completion Certification</h2>
      {document.certification.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}

      <div className="cert-sign">
        <div>
          <div className="cert-line">
            {signature ? (
              <span className="cert-signed-name">{signature.signerName}</span>
            ) : null}
          </div>
          <div className="cert-label">Homeowner Signature</div>
        </div>
        <div>
          <div className="cert-line">
            {signedOn ? <span className="cert-date">{signedOn}</span> : null}
          </div>
          <div className="cert-label">Homeowner Signature Date</div>
        </div>
        <div>
          <div className="cert-line">
            {/* The picture frozen with the certificate, never today's. */}
            {document.contractorSigner.signatureImage ? (
              <img
                src={document.contractorSigner.signatureImage}
                alt={`${document.contractorSigner.name} signature`}
              />
            ) : null}
          </div>
          <div className="cert-label">
            {document.contractorSigner.name}, {document.contractorSigner.title}
          </div>
        </div>
        <div>
          <div className="cert-line">
            <span className="cert-date">
              {certificateDateWords(document.issuedOn)}
            </span>
          </div>
          <div className="cert-label">Contractor Signature Date</div>
        </div>
      </div>
    </section>
  );
}

/**
 * The three things to do, and the form under them. The list ticks what is
 * done and points at the next, the way the agreement's does, because a
 * greyed-out button is not enough on a phone at a kitchen table.
 */
function SigningForm({
  onSign,
  signing,
  error,
}: {
  onSign: (payload: {
    consented: boolean;
    signerName: string;
    signerEmail: string;
    signedDate: string;
  }) => void | Promise<void>;
  signing?: boolean | undefined;
  error?: string | null | undefined;
}) {
  const [consented, setConsented] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signedDate] = useState(todayLocal);

  const stepInput = { consented, signerName };
  const steps = certificateSigningSteps(stepInput);
  const now = nextCertificateStep(stepInput).id;
  const focus = (step: string) => (now === step ? " pdoc-focus" : "");
  const ready = certificateSignatureIsComplete(stepInput);

  return (
    <>
      <ol className="pdoc-steps" aria-label="What to do to sign this">
        {steps.map((step) => (
          <li
            key={step.id}
            className={`${step.done ? "done" : ""}${
              step.id === now ? " now" : ""
            }`}
            aria-current={step.id === now ? "step" : undefined}
          >
            {step.label}
          </li>
        ))}
      </ol>
      <div className="pdoc-sign">
        <p className="cdoc-lead">
          Please read the certificate above, then sign it here. It takes a
          moment, and you will be emailed a signed copy.
        </p>
        <label className={`pdoc-consent${focus("consent")}`}>
          <input
            type="checkbox"
            checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
          />
          <span>
            I agree to do business electronically and to sign this certificate
            with an electronic signature.
          </span>
        </label>

        <label className={focus("name").trim()}>
          Your full name
          <input
            type="text"
            value={signerName}
            autoComplete="name"
            onChange={(e) => setSignerName(e.target.value)}
          />
        </label>

        <label>
          Your email, so we can send you the signed copy
          <input
            type="email"
            value={signerEmail}
            autoComplete="email"
            onChange={(e) => setSignerEmail(e.target.value)}
          />
        </label>

        {error ? <p className="cdoc-error">{error}</p> : null}

        <button
          type="button"
          className="cdoc-go"
          disabled={!ready || signing}
          onClick={() =>
            void onSign({
              consented,
              signerName: signerName.trim(),
              signerEmail: signerEmail.trim(),
              signedDate,
            })
          }
        >
          {signing ? "One moment…" : "Sign the certificate"}
        </button>
        <p className="cdoc-fine">
          We will email you the signed copy, and our office keeps one.
        </p>
      </div>
    </>
  );
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <div className="pdoc pdoc-message">
      <h1>{title}</h1>
      <p>{body}</p>
    </div>
  );
}
